import type { FieldValidationError } from './types.ts';

/**
 * 表单值域校验失败 —— 携带 i18n key 与变量,而不是一句写死的英文。
 *
 * 为什么需要它:payload builder 里的 `throw new Error('menu bundle slot min
 * choices is required')` 到了呈现层就没救了 ——
 *
 * - crab-console 的 `errorMessage` 只在对象同时带 `message` 与数字 `code`/`status`
 *   时才用它的文案,裸 `Error` 一律落到 `translate(fallbackKey)`。于是几十条不同的
 *   校验失败在用户眼里**塌缩成同一句通用话**,不知道是哪个字段错了。
 * - red_coral 的 `getErrorMessage` 走另一个极端:对 `Error` 直接
 *   `return response.message`,把英文技术串**原样弹给门店员工**。
 *
 * 两种都是同一个病:错误在传输中丢失身份(与 cloud 侧把 23505 塌缩成
 * `AlreadyExists` 同形)。修法是让错误自己带着能被本地化的身份走。
 *
 * `validation` 复用 `FieldValidationError` —— 它已经是 blur 校验
 * (`useBlurValidation`)的既有形状,不另造一套。
 *
 * 注意:**只用于用户可修正的值域校验**。不变量违反(`assertNever`、
 * 「unhandled presentation」、路由参数缺失)继续用裸 `Error` + 英文 fail-loud,
 * 那些是编程错误,翻译它们只会把 bug 伪装成业务提示。
 */
export class FormValidationError extends Error {
  readonly validation: FieldValidationError;

  constructor(validation: FieldValidationError) {
    super(`${validation.fieldKey}: ${validation.messageKey}`);
    this.name = 'FormValidationError';
    this.validation = validation;
  }
}

/**
 * 结构判别,不用 `instanceof`。
 *
 * 四个 app 各自 bundle,同一个 class 可能存在多份实例;`instanceof` 在那种情况下
 * 静默返回 false,错误会**悄悄退回**通用文案 —— 正是这个类要治的病。
 */
export function isFormValidationError(error: unknown): error is FormValidationError {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { name?: unknown; validation?: unknown };
  if (candidate.name !== 'FormValidationError') return false;
  const validation = candidate.validation as { fieldKey?: unknown; messageKey?: unknown } | undefined;
  return (
    typeof validation === 'object'
    && validation !== null
    && typeof validation.fieldKey === 'string'
    && typeof validation.messageKey === 'string'
  );
}

/** `throw new FormValidationError({...})` 的简写。 */
export function fieldError(
  fieldKey: string,
  messageKey: string,
  extra?: Pick<FieldValidationError, 'vars' | 'varKeys'>,
): FormValidationError {
  return new FormValidationError({ fieldKey, messageKey, ...extra });
}

/**
 * 把 `FieldValidationError` 渲染成用户看得懂的一句话。
 *
 * 两个 app 的错误呈现入口(crab-console 的 `errorMessage`、red_coral 的
 * `getErrorMessage`)都走这里,免得「varKeys 要先翻译」这条规则被实现两遍
 * 然后漂移 —— 那正是这批缺陷的成因。
 */
export function renderFieldValidationError(
  translate: (key: string, params?: Record<string, string | number>) => string,
  validation: FieldValidationError,
): string {
  const resolved: Record<string, string | number> = { ...validation.vars };
  for (const [name, key] of Object.entries(validation.varKeys ?? {})) {
    resolved[name] = translate(key);
  }
  return translate(validation.messageKey, resolved);
}

/**
 * 提交前的值域闸门:**直接跑真正的 payload builder**,把它抛出的
 * `FormValidationError` 收下来;没抛就是可以保存。
 *
 * 为什么不另写一个 `validateXxxForm`:那是同一套规则的第二份手写副本,
 * 而副本必然漂移(本 spec 附录 C)。漂移出来的东西有两种,都很难查 ——
 * 「按钮亮着但保存必失败」,以及更糟的「按钮灰着但其实存得进去」。
 * 让闸门与提交路径**在构造上同源**,漂移就不再是一种可能性。
 *
 * 代价是每次渲染多跑一次纯函数构造。这些表单的量级(几十个槽位/项)下可以忽略,
 * 而且它顺带保证了「闸门放行 ⇒ builder 一定不抛值域错误」。
 *
 * 不变量违反(裸 `Error`)照旧向上抛:那是编程错误,不该被闸门吞成
 * 「表单还没填好」—— 吞掉它等于把一个 bug 伪装成用户的问题。
 *
 * **由此产生一条纪律**:被闸门包住的 builder,里面**不能留下编辑中途可达的裸
 * `Error`**。闸门每次渲染都跑一遍 builder,一个「名称必填」写成裸 `Error` 就会
 * 在用户还没输入名字时直接把整个编辑器炸掉。落地时就踩到过一次
 * (red_coral menu-bundle 的 `requireText` 漏转),好在这类失败很响 ——
 * 组件当场崩、测试当场红,不是那种要等用户报障的静默缺陷。
 */
export function firstFormValidationError(build: () => unknown): FieldValidationError | null {
  try {
    build();
    return null;
  } catch (error) {
    if (isFormValidationError(error)) return error.validation;
    throw error;
  }
}
